import { supabase } from '@/integrations/supabase/client'
import { API_BASE_URL } from '@/utils/apiConfig'

async function writeVideoRecord(payload: Record<string, unknown>) {
    const response = await fetch(`${API_BASE_URL}/api/video-uploads/record`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
    if (!response.ok) throw new Error(`Video record update failed: ${response.status}`)
    return response.json()
}

export type VideoRecordStatus =
    | 'initializing'
    | 'uploading'
    | 'finalizing'
    | 'completed'
    | 'failed'
    | 'aborted'

export interface VideoRecordStatusUpdate {
    employee_test_id: string
    upload_id: string
    status: VideoRecordStatus
    bucket_path?: string
    has_audio?: boolean
    company_id?: string
}

/**
 * Initialize a new video record with multi_upload type and initializing status
 */
export async function initializeVideoRecordStatus(
    employeeTestId: string,
    uploadId: string,
    bucketPath: string,
    companyId?: string,
    attemptNumber?: number,
    candidateToken?: string
): Promise<void> {
    await writeVideoRecord({
        action: 'initialize', assignmentId: employeeTestId, candidateToken,
        uploadId, key: bucketPath, attemptNumber, companyId,
    })
}

/**
 * Update the status of a video record by upload_id
 */
export async function updateVideoRecordStatus(
    employeeTestId: string,
    uploadId: string,
    status: VideoRecordStatus,
    additionalData?: {
        bucket_path?: string
        has_audio?: boolean
    },
    candidateToken?: string,
    bucketPath?: string
): Promise<void> {
    await writeVideoRecord({
        action: 'update', assignmentId: employeeTestId, candidateToken,
        uploadId, key: bucketPath, status, hasAudio: additionalData?.has_audio,
    })
}

/**
 * Get the current status of a video record by upload_id
 */
export async function getVideoRecordStatus(
    employeeTestId: string,
    uploadId: string
): Promise<VideoRecordStatus | null> {
    try {
        const { data, error } = await supabase
            .from('video_records')
            .select('status')
            .eq('employee_test_id', employeeTestId)
            .eq('upload_id', uploadId)
            .eq('recording_type', 'multi_upload')
            .single()

        if (error) {
            if (error.code === 'PGRST116') {
                // No rows found
                return null
            }
            console.error('[VideoRecordStatus] Failed to get video record status:', error)
            throw error
        }

        return data?.status as VideoRecordStatus || null
    } catch (error) {
        console.error('[VideoRecordStatus] Error getting video record status:', error)
        throw error
    }
}

/**
 * Get all video records for a specific employee test with multi_upload type
 */
export async function getMultiUploadVideoRecords(
    employeeTestId: string
): Promise<Array<{
    id: string
    upload_id: string
    status: VideoRecordStatus
    bucket_path: string
    has_audio: boolean | null
    recorded_at: string
    updated_at: string | null
}>> {
    try {
        const { data, error } = await supabase
            .from('video_records')
            .select('id, upload_id, status, bucket_path, has_audio, recorded_at, updated_at')
            .eq('employee_test_id', employeeTestId)
            .eq('recording_type', 'multi_upload')
            .order('recorded_at', { ascending: true })

        if (error) {
            console.error('[VideoRecordStatus] Failed to get multi upload video records:', error)
            throw error
        }

        return (data || []).map(record => ({
            ...record,
            status: record.status as VideoRecordStatus
        }))
    } catch (error) {
        console.error('[VideoRecordStatus] Error getting multi upload video records:', error)
        throw error
    }
}

/**
 * Get video records by upload_id and employee_test_id for status tracking
 */
export async function getVideoRecordByUploadId(
    employeeTestId: string,
    uploadId: string
): Promise<{
    id: string
    upload_id: string
    status: VideoRecordStatus
    bucket_path: string
    has_audio: boolean | null
    recorded_at: string
    updated_at: string | null
    company_id: string | null
} | null> {
    try {
        const { data, error } = await supabase
            .from('video_records')
            .select('id, upload_id, status, bucket_path, has_audio, recorded_at, updated_at, company_id')
            .eq('employee_test_id', employeeTestId)
            .eq('upload_id', uploadId)
            .eq('recording_type', 'multi_upload')
            .single()

        if (error) {
            if (error.code === 'PGRST116') {
                // No rows found
                return null
            }
            console.error('[VideoRecordStatus] Failed to get video record by upload ID:', error)
            throw error
        }

        return data ? {
            ...data,
            status: data.status as VideoRecordStatus
        } : null
    } catch (error) {
        console.error('[VideoRecordStatus] Error getting video record by upload ID:', error)
        throw error
    }
}

/**
 * Store a simple video record (for non-multi-upload cases like interview/environment recordings)
 */
export async function storeSimpleVideoRecord(
    employeeTestId: string,
    videoPath: string,
    type: string,
    companyId: string,
    options?: {
        is_random?: boolean
        attempt_number?: number
        candidateToken?: string
    }
): Promise<string> {
    const result = await writeVideoRecord({
        action: 'simple', assignmentId: employeeTestId, candidateToken: options?.candidateToken,
        key: videoPath, recordingType: type === 'environment' ? 'environment' : 'interview',
        attemptNumber: options?.attempt_number, isRandom: options?.is_random, companyId,
    })
    return result.id
}
